import { useEffect, useMemo, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import { GrClose } from 'react-icons/gr';
import { FaArrowDown, FaArrowUp, FaSearch } from 'react-icons/fa';

import FileStatusBadge from '../FileStatusBadge';
import {
  formatDateCellShort,
  formatNumberWithCommas,
} from '../../helpers/reactTable';

// Keep this in sync with the FILES_QUERY response shape and with
// routes/mywork/UploadedFiles.tsx so the two surfaces feel consistent.
export interface UploadedFile {
  id: string;
  name: string;
  records?: number | null;
  numUniqueRecords?: number | null;
  numMatchedRecords?: number | null;
  matchRate?: number | null;
  status: string;
  createdAt: string;
  crawledAt?: string | null;
  errorStage?: string | null;
  errorMessage?: string | null;
  /** ATAD-136: 'id' (default when absent), 'zip' or (ATAD-146) 'model'. */
  kind?: string | null;
  numSkippedRecords?: number | null;
  /** ATAD-146: the Alliant data source it was imported from; null for uploads. */
  dataSource?: string | null;
}

interface UploadedFilesModalProps {
  open: boolean;
  onClose: () => void;
  files: UploadedFile[];
  loading?: boolean;
  /**
   * Returns true if the file can still be added to the given zone (i.e. it is
   * not already present in that zone or its opposite). Wired to the builder's
   * canAddMyDataResource helper at the call site so the modal stays
   * presentational and the eligibility rules live in one place.
   */
  canAddToZone: (
    fileId: string,
    zoneType: 'include' | 'exclude'
  ) => boolean;
  onAddFile: (file: UploadedFile, zoneType: 'include' | 'exclude') => void;
}

// Status labels — duplicated lightly from FileStatusBadge so the search box
// can match the user-facing label (e.g. typing "failed" finds "Upload failed",
// "Processing failed", etc.). Keep these aligned with STATUS_PRESENTATION
// in components/FileStatusBadge.tsx.
const STATUS_SEARCH_LABEL: Record<string, string> = {
  uploading: 'Uploading',
  pending: 'Pending',
  processing: 'Processing',
  matching: 'Matching',
  matched: 'Indexing',
  completed: 'Completed',
  'uploading-failed': 'Upload failed',
  'processing-failed': 'Processing failed',
  'matching-failed': 'Matching failed',
  failed: 'Failed',
};

const FAILED_STATUSES = new Set([
  'uploading-failed',
  'processing-failed',
  'matching-failed',
  'failed',
]);

function statusSearchLabel(status: string): string {
  return STATUS_SEARCH_LABEL[status] ?? status.replace(/-/g, ' ');
}

/**
 * Modal listing every uploaded file with search + sort. Mirrors the column
 * layout used in routes/mywork/UploadedFiles.tsx so the same fields the user
 * sees on the My Work table are available here, but the action set is scoped
 * to "add to this audience" so the workflow stays inside the builder.
 *
 * Live progress (status badge + counts) updates whenever the parent re-renders
 * with fresh data — the parent owns polling on FILES_QUERY.
 */
export default function UploadedFilesModal({
  open,
  onClose,
  files,
  loading,
  canAddToZone,
  onAddFile,
}: UploadedFilesModalProps) {
  const [search, setSearch] = useState('');

  // Reset the search term whenever the modal is closed so the next open is
  // not pre-filtered by a stale query.
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  // Close on Escape — table modals without keyboard dismissal feel broken.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Filter by file name, status label (human-readable), or matched count.
  // Matched count match is a substring on the digits — typing "1000" finds
  // any file with "1000" appearing in its match count (e.g. 10000, 21000).
  const filteredFiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return files;
    return files.filter((file) => {
      const name = (file.name ?? '').toLowerCase();
      const statusLabel = statusSearchLabel(file.status).toLowerCase();
      const matchedStr =
        file.numMatchedRecords != null ? String(file.numMatchedRecords) : '';
      return (
        name.includes(q) ||
        statusLabel.includes(q) ||
        matchedStr.includes(q)
      );
    });
  }, [files, search]);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ value }: { value: string }) => (
          <span className="font-medium text-gray-900 break-all">{value}</span>
        ),
      },
      {
        Header: 'Uploaded Date',
        accessor: 'createdAt',
        Cell: formatDateCellShort,
      },
      {
        Header: 'Uploaded',
        accessor: 'records',
        Cell: ({ value }: { value: number | null | undefined }) =>
          value != null
            ? formatNumberWithCommas({ value, ifEmpty: '—' })
            : '—',
      },
      {
        Header: 'Unique',
        accessor: 'numUniqueRecords',
        Cell: ({ value }: { value: number | null | undefined }) =>
          value != null
            ? formatNumberWithCommas({ value, ifEmpty: '—' })
            : '—',
      },
      {
        Header: 'Matched',
        accessor: 'numMatchedRecords',
        Cell: ({ value }: { value: number | null | undefined }) =>
          value != null
            ? formatNumberWithCommas({ value, ifEmpty: '—' })
            : '—',
      },
      {
        Header: 'Match rate',
        accessor: 'matchRate',
        Cell: ({ value }: { value: number | null | undefined }) =>
          value != null ? `${(value * 100).toFixed(2)}%` : '—',
      },
      {
        Header: 'Status',
        accessor: 'status',
        // Sort by raw status string so all 'completed' rows group together.
        Cell: ({ row }: any) => (
          <FileStatusBadge
            status={row.original.status}
            imported={!!row.original.dataSource}
          />
        ),
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const file = row.original as UploadedFile;
          const isCompleted = file.status === 'completed';
          const isFailed = FAILED_STATUSES.has(file.status);

          // Non-completed files (in-progress or failed) cannot be added to a
          // builder zone — drag/click is gated on crawledAt elsewhere in the
          // app, and we surface that the same way here.
          const disableReason = !isCompleted
            ? isFailed
              ? 'File processing failed'
              : 'File is still processing'
            : null;

          const canInclude =
            isCompleted && canAddToZone(file.id, 'include');
          const canExclude =
            isCompleted && canAddToZone(file.id, 'exclude');

          if (disableReason) {
            return (
              <span className="text-xs text-gray-400 italic">
                {disableReason}
              </span>
            );
          }

          return (
            <div className="flex flex-row gap-2">
              <button
                type="button"
                onClick={() => onAddFile(file, 'include')}
                disabled={!canInclude}
                className={`text-xs px-3 py-1 rounded-full font-semibold border ${
                  canInclude
                    ? 'bg-primary border-primary-dark text-black hover:opacity-80'
                    : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title={
                  !canInclude
                    ? 'Already added to the audience'
                    : 'Add to Include'
                }
              >
                Add to Include
              </button>
              <button
                type="button"
                onClick={() => onAddFile(file, 'exclude')}
                disabled={!canExclude}
                className={`text-xs px-3 py-1 rounded-full font-semibold border ${
                  canExclude
                    ? 'bg-white border-primary-dark text-primary-dark hover:bg-primary hover:bg-opacity-10'
                    : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title={
                  !canExclude
                    ? 'Already added to the audience'
                    : 'Add to Exclude'
                }
              >
                Add to Exclude
              </button>
            </div>
          );
        },
      },
    ],
    [canAddToZone, onAddFile]
  );

  const initialSortBy = useMemo(
    () => [{ id: 'createdAt', desc: true }],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: filteredFiles,
      initialState: {
        sortBy: initialSortBy,
      },
    },
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center items-center"
      aria-modal="true"
      role="dialog"
    >
      {/* Background overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="relative z-50 flex flex-col w-11/12 lg:w-5/6 xl:w-4/5 max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h4 className="text-xl font-semibold text-gray-800">
              All Uploaded Files
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              Search, sort, and add any of your uploaded files to this
              audience.
            </p>
          </div>
          <button
            className="p-2 text-gray-400 rounded-full hover:bg-gray-100 hover:text-gray-600 transition-colors"
            onClick={onClose}
            aria-label="Close modal"
          >
            <GrClose size={20} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="relative">
            <FaSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={14}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, status, or matched count..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              autoFocus
            />
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
            <span>
              {filteredFiles.length} of {files.length}{' '}
              {files.length === 1 ? 'file' : 'files'}
            </span>
            {loading && <span className="italic">Refreshing…</span>}
          </div>
        </div>

        {/* Table */}
        <div className="flex-grow overflow-auto">
          {files.length === 0 ? (
            <p className="text-center py-12 text-gray-500">
              You haven't uploaded any files yet.
            </p>
          ) : filteredFiles.length === 0 ? (
            <p className="text-center py-12 text-gray-500">
              No files match "{search}".
            </p>
          ) : (
            <table {...getTableProps()} className="w-full text-sm">
              <thead className="sticky top-0 bg-white shadow-[inset_0_-2px_0_0_#000] z-10">
                {headerGroups.map((headerGroup) => {
                  const { key, ...rest } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr key={key} {...rest}>
                      {headerGroup.headers.map((column) => {
                        const { key: colKey, ...colRest } =
                          column.getHeaderProps(
                            column.getSortByToggleProps()
                          );
                        return (
                          <th
                            key={colKey}
                            {...colRest}
                            className="text-left p-3 font-semibold text-gray-700 whitespace-nowrap hover:bg-gray-50"
                          >
                            <div className="flex items-center">
                              {column.render('Header')}
                              {column.isSorted && (
                                <span className="pl-2">
                                  {column.isSortedDesc ? (
                                    <FaArrowDown size={10} />
                                  ) : (
                                    <FaArrowUp size={10} />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  const { key, ...rowRest } = row.getRowProps();
                  return (
                    <tr
                      key={key}
                      {...rowRest}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      {row.cells.map((cell) => {
                        const { key: cellKey, ...cellRest } =
                          cell.getCellProps();
                        return (
                          <td
                            key={cellKey}
                            {...cellRest}
                            className="p-3 align-middle whitespace-nowrap"
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
